import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'src/containers/withRouter/withRouter';
import moment from 'moment';
import { withTheme } from 'styled-components';
import {
  CONSENT_CATEGORIES,
  ErrorBoundary,
  GlobalBreadcrumb,
  ResizeListener,
  parseQuery,
  Layout,
  Loader,
  runWhenCategoryAllowed,
} from '@jjj/components';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { getCurrentCountryContext } from '../../helpers/countriesHelpers';
import authAction from '../../redux/auth/actions';
import appActions from '../../redux/app/actions';
import notificationActions from '../../redux/notifications/actions';
import requestStatusActions from '../../redux/requestStatus/actions';
import Sidebar from '../Sidebar/Sidebar';
import Topbar from '../Topbar/Topbar';
import AppRouter, { ROUTES } from './AppRouter';
import { isDevOrReviewOrStagingOrDemo, getIsIntercomEnabled } from '../../settings';
import AppHolder from './commonStyle';
import FooterContent from '../footerContent/footerContent';
import './global.css';
import { AppContent, AppFooter } from './App.style';
import { bootIntercomChat, loadIntercomWidget } from '../../helpers/intercomChat';
import { breadcrumbHomeIconClickedGAEvent } from '../../helpers/googleAnalytics/events';
import { AUTH_KEY } from '../../helpers/localStorageKeys.const';
import i18n from '../../i18n';
import { getDateFormat } from '../../helpers/dateTime';
import appUserBootstrapActions from '../../redux/appUserBootstrap/actions';

library.add(fas);

const { logout } = authAction;
const { toggleAll } = appActions;

const routeMatcher = route => new RegExp(`^/${route.replace(/:[^/]+/g, '[^/]+')}$`);

export class App extends Component {
  componentDidMount() {
    const { appUserBootstrap, changeCurrent, location } = this.props;
    appUserBootstrap();
    window.addEventListener('storage', this.autoLogout);
    this.setNotificationToRead();

    this.cancelIntercomConsent = runWhenCategoryAllowed(CONSENT_CATEGORIES.functional, () => {
      if (this.props.auth?.intercom?.hmac && getIsIntercomEnabled()) {
        loadIntercomWidget();
        this.initIntercomChat();
      }
    });

    this.props.setInitBreadcrumbState();

    // highlight the current menu item on direct page load
    changeCurrent([location.pathname.split('/')[1]]);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.pathname !== this.props.location.pathname) {
      this.props.setPendingToFalse();
    }
  }

  componentWillUnmount() {
    clearInterval(this.interval);
    window.removeEventListener('storage', this.autoLogout);
    // Consent may be granted long after this unmounts — logging out, for instance. Without this
    // the deferred callback would still boot Intercom against a dead component.
    this.cancelIntercomConsent?.();
  }

  setNotificationToRead() {
    const query = parseQuery(this.props.location.search);

    if (query.notification_id && localStorage.getItem('notification_id') !== query.notification_id) {
      localStorage.setItem('notification_id', query.notification_id);
      this.props.notificationMarkAsRead({ id: query.notification_id });
    }
  }

  autoLogout = event => {
    // Check if user has been logged out in some of the browser tab (deleted 'session' Local Storage item)
    if (event.key === AUTH_KEY && event.newValue === null) {
      this.props.logout();
    }
  };

  initIntercomChat = () => {
    const {
      id,
      intercom: { hmac },
      profile: { email, name },
    } = this.props.auth;

    bootIntercomChat(email, id, name, hmac);
  };

  isDashboard = () => this.props.location.pathname.startsWith('/dashboard');

  is404Page = () => this.props.location.pathname === '/404';

  // These pages carry their own omnibar, so breadcrumbs and footer are dropped to avoid duplicating it.
  isRateEngineEditorPage = () => {
    const { pathname } = this.props.location;

    return (
      pathname === `/${ROUTES.RATE_ENGINE_CREATE}` ||
      routeMatcher(ROUTES.RATE_ENGINE_EDIT).test(pathname) ||
      routeMatcher(ROUTES.RATE_ENGINE_VERSION_CREATE).test(pathname)
    );
  };

  // The rate card wizard and review flow carry their own omnibar, which already names the page and
  // owns the way back, so the breadcrumb trail above it would be a second, competing one.
  isRateCardsEditorPage = () => {
    const { pathname } = this.props.location;
    const reviewPattern = new RegExp(`^/${ROUTES.RATE_CARDS_REVIEW.replace(':id', '[^/]+')}$`);

    return pathname === `/${ROUTES.RATE_CARDS_CREATE}` || reviewPattern.test(pathname);
  };

  // The Rate Simulator carries its own Omnibar header, so it drops the global breadcrumb row.
  isRateSimulatorPage = () => this.props.location.pathname === `/${ROUTES.RATE_SIMULATOR}`;

  isPositionAvailabilityPage = () => this.props.location.pathname === `/${ROUTES.POSITION_AVAILABILITY}`;

  // The engine-bound agency contract editors carry their own omnibar; the contract page itself does
  // not, and keeps the trail (the breadcrumb map names its last crumb after the agency).
  isAgencyContractEditorPage = () => {
    const { pathname } = this.props.location;

    return (
      pathname === `/${ROUTES.AGENCY_CONTRACT_V2_CREATE}` ||
      routeMatcher(ROUTES.AGENCY_CONTRACT_V2_EDIT).test(pathname) ||
      routeMatcher(ROUTES.AGENCY_CONTRACT_V2_SCOPE).test(pathname)
    );
  };

  getGlobalBreadcrumbs = () => {
    if (this.isDashboard()) {
      return <div className="currentDate">{moment().format(getDateFormat('date_full'))}</div>;
    }

    if (
      this.is404Page() ||
      this.isRateEngineEditorPage() ||
      this.isRateCardsEditorPage() ||
      this.isRateSimulatorPage() ||
      this.isPositionAvailabilityPage() ||
      this.isAgencyContractEditorPage() ||
      this.props.location.pathname.includes('checklists')
    ) {
      return <div className="empty-div" />;
    }
    const { globalState, app, location } = this.props;
    const { breadcrumbState } = app;

    return (
      <GlobalBreadcrumb
        location={location}
        onHomeIconClick={breadcrumbHomeIconClickedGAEvent}
        breadcrumbState={breadcrumbState}
        globalState={globalState}
      />
    );
  };

  isfFullWindowContent = () => {
    const { history } = this.props;

    return history?.location?.pathname === `/${ROUTES.ADMIN_SIGN_IN}`;
  };

  render() {
    const { url } = this.props.match;
    const { theme, app, auth, featureFlags, isLoading } = this.props;
    // Force rerender app when lang or country or time zone is changed
    const appKey = `${i18n.language}-${getCurrentCountryContext().code}-${auth.time_zone}`;

    const { height } = app;
    const appHeight = window.innerHeight;

    const isFullWindowContent = this.isfFullWindowContent();
    const isFooterHidden = isFullWindowContent || this.isRateEngineEditorPage() || this.isRateCardsEditorPage();

    return (
      <AppHolder>
        <Layout style={{ height: appHeight }}>
          <ResizeListener
            onResize={windowSize =>
              this.props.toggleAll({
                width: windowSize.windowWidth,
                height: windowSize.windowHeight,
              })
            }
          />
          {!isFullWindowContent && <Topbar url={url} />}
          <Layout style={{ flexDirection: 'row', overflowX: 'hidden' }}>
            {!isFullWindowContent && <Sidebar url={url} key={i18n.language} />}
            <Layout
              className="isoContentMainLayout"
              id="main-layout"
              style={{
                height,
                position: 'relative',
              }}
            >
              {!isFullWindowContent && this.getGlobalBreadcrumbs()}
              <AppContent key={appKey} className="isomorphicContent">
                <ErrorBoundary>
                  {isLoading ? (
                    <Loader />
                  ) : (
                    <AppRouter url={url} key={appKey} role={auth.role} featureFlags={featureFlags} />
                  )}
                </ErrorBoundary>
              </AppContent>
              {!isFooterHidden && (
                <AppFooter className="appFooter">
                  <FooterContent theme={theme} isApiDocsAvailable={isDevOrReviewOrStagingOrDemo} />
                </AppFooter>
              )}
            </Layout>
          </Layout>
        </Layout>
      </AppHolder>
    );
  }
}

export default connect(
  state => ({
    auth: state.Auth,
    app: state.App,
    isRequestPending: state.RequestStatus,
    globalState: state,
    featureFlags: state.StartAppSettings?.feature_flags,
    isLoading: state.AppUserBootstrap.isLoading,
  }),
  dispatch => ({
    toggleAll: /* istanbul ignore next */ data => dispatch(toggleAll(data)),
    logout: /* istanbul ignore next */ () => dispatch(logout()),
    notificationMarkAsRead: /* istanbul ignore next */ data => dispatch(appActions.notificationMarkAsRead(data)),
    setPendingToFalse: /* istanbul ignore next */ () => dispatch(requestStatusActions.setPendingToFalse()),
    setInitBreadcrumbState: /* istanbul ignore next */ () => dispatch(appActions.setInitBreadcrumbState()),
    changeCurrent: /* istanbul ignore next */ data => dispatch(appActions.changeCurrent(data)),
    addNotification: /* istanbul ignore next */ data => dispatch(notificationActions.addNotification(data)),
    appUserBootstrap: /* istanbul ignore next */ () => dispatch(appUserBootstrapActions.appUserBootstrap()),
  }),
)(withRouter(withTheme(App)));
